import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { More } from '@travel/icons/ui';
import { Translate } from '@travel/translation';
import { DESKTOP_SCREEN, useDeviceType } from '@travel/traveler-core/hooks';
import useDialogHandler from '@travel/traveler-core/hooks/useDialogHandler';
import { Balloon } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import { openQrDialog } from 'store/reservationsList/actions';

import type { ReservationType } from 'constants/reservations';
import { ServiceType } from 'constants/reservations';
import Link from 'core/universalRouter/Link';
import paths from 'core/universalRouter/paths';
import type { QuickActions as QuickActionsType } from 'ReservationsList-Types';
import { formatQuickActions } from 'utils/quickActions';
import type { UrlParameter } from 'utils/quickActionsUrl';
import { bookingDetails } from 'utils/quickActionsUrl';

import JRReservationDetailButton from '../JRReservationDetailButton';

import styles from './quickActions.module.scss';
type Props = {
  quickActions: QuickActionsType;
  reservationDetails: UrlParameter;
  reservationType: ReservationType;
};

const QuickActions = (props: Props) => {
  const { quickActions, reservationDetails } = props;

  const { isOpen, onOpen, onClose } = useDialogHandler(false);

  const deviceType = useDeviceType();
  const isPc = deviceType === DESKTOP_SCREEN;
  const isJR = reservationDetails.serviceType === ServiceType.JR;
  const isDomestic = reservationDetails.serviceType === ServiceType.DOMESTIC;
  const dispatch = useDispatch();
  const formattedActions = formatQuickActions(quickActions, reservationDetails, deviceType);
  const isShowOthersMenu = formattedActions.length >= 5;
  const lengthClass = `action-${isShowOthersMenu ? 4 : formattedActions.length}`;

  const handleDialogue = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    dispatch(openQrDialog(reservationDetails.qrCodeId || ''));
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('click', onClose);
    }

    return () => {
      if (isOpen) {
        document.removeEventListener('click', onClose);
      }
    };
  }, [isOpen, onClose]);

  if (!isPc && !isNotEmptyArray(formattedActions)) return null;

  return (
    <div className={styles.container}>
      {isPc &&
        (isJR ? (
          <JRReservationDetailButton
            className={styles.jrDetailButton}
            externalId={reservationDetails.externalId}
            reservationId={reservationDetails.reservationId}
          />
        ) : (
          <>
            {isDomestic && reservationDetails?.reservationId ? (
              <Link
                to={`${paths.detail.pathResolver(reservationDetails?.reservationId)}?type=${props.reservationType}`}
                className={cx(styles[lengthClass], styles.detail)}
                data-testid={`quickAction-link-detail`}
              >
                <div className={styles.actionText}>
                  <Translate id="Booking_List.Reservation_Summary.Navigation.Details" />
                </div>
              </Link>
            ) : (
              <a
                className={cx(styles[lengthClass], styles.detail)}
                key="detail"
                href={bookingDetails({
                  serviceType: reservationDetails.serviceType,
                  reservationId: reservationDetails.reservationId,
                  providerId: reservationDetails.providerId,
                  checkInDate: reservationDetails.checkInDate,
                  checkOutDate: reservationDetails.checkOutDate,
                  reservedSkyrepNo: reservationDetails.reservedSkyrepNo,
                })}
                target={'_self'}
                data-testid={`quickAction-link-detail`}
              >
                <div className={styles.actionText}>
                  <Translate id="Booking_List.Reservation_Summary.Navigation.Details" />
                </div>
              </a>
            )}
          </>
        ))}
      {formattedActions.map(
        ({ url, icon, key, text, isNewTab, isQrCode }, index) =>
          (isPc || (!isPc && !isShowOthersMenu) || (!isPc && isShowOthersMenu && index < 3)) && (
            <a
              className={cx(styles[lengthClass], styles[key])}
              key={key}
              href={url}
              target={isNewTab ? '_blank' : '_self'}
              rel={`noreferrer noopener`}
              onClick={isQrCode ? handleDialogue : undefined}
              data-testid={`quickAction-link-${key}`}
            >
              <div className={cx(key === 'qrCheckIn' ? styles.showIcon : '', styles.iconContainer)}>
                {icon}
              </div>
              <div className={styles.actionText}>{text}</div>
            </a>
          ),
      )}
      {!isPc && isShowOthersMenu && (
        <button
          className={cx(styles[lengthClass], styles.otherButton)}
          onClick={onOpen}
          data-testid="quickAction-link-others"
        >
          <div className={styles.iconContainer}>
            <More color="cilantro" size={32} />
          </div>
          <div className={styles.actionText}>
            <Translate id="Booking_List.Reservation_Summary.Navigation.Others" />
          </div>
          {isOpen && (
            <Balloon
              direction="bottomRight"
              hasContentMinHeight={false}
              className={styles.balloon}
              triangleClassName={styles.triangle}
            >
              <ul className={styles.content}>
                {formattedActions?.map(({ url, icon, key, text, isNewTab, isQrCode }, index) => (
                  <li
                    className={styles.actionLink}
                    key={key}
                    data-testid={`quickAction-link-${key}`}
                  >
                    <a
                      key={key}
                      href={url}
                      target={isNewTab ? '_blank' : '_self'}
                      rel={`noreferrer noopener`}
                      onClick={isQrCode ? handleDialogue : undefined}
                      data-testid={`quickAction-link-${key}`}
                    >
                      {text}
                    </a>
                  </li>
                ))}
              </ul>
            </Balloon>
          )}
        </button>
      )}
    </div>
  );
};

export default QuickActions;
